"use client";

import { DELIMITER, PAGESIZE_KEYWORD } from "@/lib/searchspring/constants";
import {
	EnrichedSearchRequestModel,
	ToApplyFacetData,
} from "@/lib/searchspring/types";
import { getAppliedFacets } from "@/lib/searchspring/utils";
import {
	MetaResponseModel,
	MetaResponseModelSortOption,
	SearchResponseModelFacet,
	SearchResponseModelSorting,
} from "@searchspring/snapi-types";
import { useSearchParams } from "next/navigation";
import {
	createContext,
	Dispatch,
	SetStateAction,
	useEffect,
	useState,
} from "react";

interface SearchSpringContextProps {
	sortOptions: Array<MetaResponseModelSortOption>;
	sorting: Array<SearchResponseModelSorting>;
	facets: Array<SearchResponseModelFacet>;
	sortBy: MetaResponseModelSortOption | undefined;
	pageSize: number | undefined;
	facetsToApply: ToApplyFacetData[];
	setSortBy: (sort: MetaResponseModelSortOption | undefined) => void;
	setPageSize: (pageSize: number | undefined) => void;
	handleAddFacet: (data: ToApplyFacetData) => void;
	handleRemoveFacet: (facetId: string) => void;
	reset: () => void;
	setFacetsToApply: Dispatch<SetStateAction<ToApplyFacetData[]>>;
}

export const SearchSpringContext = createContext(
	{} as SearchSpringContextProps,
);
interface ProviderProps {
	meta: MetaResponseModel;
	results: EnrichedSearchRequestModel;
	children: React.ReactNode;
}

export const SearchSpringContextProvider = ({
	meta,
	results,
	children,
}: ProviderProps) => {
	const getAppliedSortBy = () => {
		const sort = Array.from(searchParams.entries()).find(([key]) =>
			key.startsWith("sort"),
		)?.[1];

		if (!sort) return;

		const [field, direction] = sort?.split(DELIMITER);

		return meta?.sortOptions?.find(
			(option) => option.field === field && option.direction === direction,
		);
	};

	const getAppliedPageSize = () => {
		return Number(searchParams.get(PAGESIZE_KEYWORD)) || undefined;
	};
	const searchParams = useSearchParams();
	const appliedFacets = getAppliedFacets(results.facets);
	const [facetsToApply, setFacetsToApply] =
		useState<ToApplyFacetData[]>(appliedFacets);
	const [sortBy, setSortBy] = useState<MetaResponseModelSortOption | undefined>(
		getAppliedSortBy(),
	);
	const [pageSize, setPageSize] = useState<number | undefined>(
		getAppliedPageSize(),
	);

	const handleAddFacet = (data: ToApplyFacetData) => {
		setFacetsToApply((prev) => {
			let updated = [...prev];

			const index = updated.findIndex(
				(item) => item.params.join("") === data.params.join(""),
			);

			if (index !== -1) {
				updated = updated.filter(
					(item) => item.params.join("") !== data.params.join(""),
				);
			} else {
				updated.push(data);
			}

			return updated;
		});
	};
	const handleRemoveFacet = (facetId: string) => {
		setFacetsToApply((prev) =>
			prev.filter((facet) => facet.params.join("") !== facetId),
		);
	};

	const reset = () => {
		setSortBy(getAppliedSortBy());
		setPageSize(getAppliedPageSize());
		setFacetsToApply(appliedFacets);
	};

	const value = {
		sortOptions: meta.sortOptions || [],
		sorting: results.sorting || [],
		facets: results.facets || [],
		sortBy,
		pageSize,
		facetsToApply,
		setSortBy,
		setPageSize,
		handleAddFacet,
		handleRemoveFacet,
		reset,
		setFacetsToApply,
	};

	return (
		<SearchSpringContext.Provider value={value}>
			{children}
		</SearchSpringContext.Provider>
	);
};
